import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Optional } from '@angular/core';
import { of, retry, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export class ConfigServiceConfig {
    config?: any; // Configuration object to pre-populate the service
    url?: string; // URL to retrieve configuration from the server
    serverMapper?: (data: any) => any; // Mapper function to translate server object to config object
}

@Injectable({providedIn: 'root'})
export class ConfigService {
    // TODO: Both Cloud/Alerting want to remove this file (was from old common)
    private emitters = {};
    public config: any;

    static MergeDefaults(defaults, overrides) {
        //TODO: Deep assign
        return Object.assign({}, defaults, overrides);
    }

    /**
     * Initialization helper to support Optional() implementations of ConfigService
     *
     * @param service nullable config service instance to apply defaults to
     * @param namespace namespace storage for config
     * @param defaults defaults of the service/component
     */
    static SafeInit(service: ConfigService, namespace: string, defaults: any) {
        if (service) {
            return service.setDefaults(namespace, defaults);
        } else {
            return defaults;
        }
    }

    constructor(@Optional() private http: HttpClient, @Optional() private c: ConfigServiceConfig) {
        c.config = environment;
        const config = c ? Object.assign({}, (c.config || {})) : {};

        if (this.c && this.http) {
            if (this.c.url) {
                this.http.get(c.url)
                .pipe(
                    retry(3),
                    map(serverConfig => {
                        const data = c.serverMapper ? c.serverMapper(serverConfig) : serverConfig;
                        return Object.assign(config, data);
                    }),
                    catchError(err => {
                        console.error(err);
                        return of(err);
                    })
                ).toPromise();
            }
        }

        this.config = config;
    }

    public get(path) {
        return path.split('.').reduce((obj, key) => (obj && obj[key]) ? obj[key] : undefined, this.config);
    }

    public getCopy(path) {
        const val = this.get(path);
        return val === undefined ? undefined : JSON.parse(JSON.stringify(val));
    }

    public set(path, val) {
        const arr = path.split('.'),
            curNs = [],
            toUpdate = [];
        let temp = this.config;

        // Loop through intermediary objects
        for (let i = 0; i < arr.length - 1; i++) {
            temp = temp[arr[i]] = temp[arr[i]] || {};

            // Test for emitter on intermediary
            curNs.push(arr[i]);
            const namespace = curNs.join('.');
            if (this.emitters[namespace]) {
                toUpdate.push({ emitter: this.emitters[namespace], value: temp });
            }
        }

        // End of namespace - assign value
        temp[arr[arr.length - 1]] = val;

        // Check for emitter on end
        curNs.push([arr[arr.length - 1]]);
        const ns = curNs.join('.');
        if (this.emitters[ns]) {
            toUpdate.push({ emitter: this.emitters[ns], value: val });
        }

        // Fire emitters after everything is set
        toUpdate.forEach((ev) => {
            ev.emitter.emit(ev.value);
        });
    }

    setDefaults(namespace: string, defaults: any) {
        const curVal = this.get(namespace),
            merged = ConfigService.MergeDefaults(defaults, curVal);

        this.set(namespace, merged);

        return merged;
    }

    public subscribe(namespace, cb): Subscription {
        this.emitters[namespace] = this.emitters[namespace] || new EventEmitter();
        return this.emitters[namespace].subscribe(cb);
    }
}
